
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import Head from 'next/head';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import { getHourlyWaitAverages } from '../../lib/dbFetcher';
import { DEPARTURE_HOURS, pad, formatHour, hourSlugs, arrivalHourFor, arrivalAvgFor, byHourMap } from '../../lib/bestTimeToArrive';
const CPH_CHECKIN_URL = {
    en: 'https://www.cph.dk/en/practical/check-in',
    da: 'https://www.cph.dk/praktisk/check-in'
};
export async function getStaticPaths({ locales }) {
    const paths = (locales || ['da']).flatMap((locale) => hourSlugs().map((hour) => ({ params: { hour }, locale })));
    return { paths, fallback: false };
}
async function getStaticProps({ params, locale }) {
    const depHour = Number(params.hour);
    if (!DEPARTURE_HOURS.includes(depHour))
        return { notFound: true };
    let hourly = [];
    try {
        hourly = await getHourlyWaitAverages();
    }
    catch (error) {
        console.error('Error building best-time-to-arrive page:', error);
        hourly = Array.from({ length: 24 }, (_, hour) => ({
            hour,
            avgLow: null
        }));
    }
    const byHour = byHourMap(hourly);
    const arrivalHour = arrivalHourFor(depHour);
    const arrivalAvg = arrivalAvgFor(depHour, byHour);
    // The arrival window: the few hours leading up to (and including) the
    // recommended "through security by" time. Each row is a real data point.
    const windowHours = [arrivalHour - 1, arrivalHour, arrivalHour + 1]
        .filter((h) => h >= 0 && h <= 23)
        .map((h) => ({
        hour: h,
        avgLow: byHour.has(h) ? byHour.get(h) : null,
        recommended: h === arrivalHour
    }));
    // All-day average across hours that actually have data, for context.
    const known = hourly.filter((h) => h.avgLow != null).map((h) => h.avgLow);
    const dayAvg = known.length
        ? Math.round((known.reduce((s, v) => s + v, 0) / known.length) * 10) /
            10
        : null;
    return {
        props: {
            depHour,
            arrivalHour,
            arrivalAvg,
            windowHours,
            dayAvg,
            locale: locale || 'da'
        },
        revalidate: 3600
    };
}
function BestTimeToArrive({ depHour, arrivalHour, arrivalAvg, windowHours, dayAvg }) {
    const { t, lang } = useTranslation('best-time-to-arrive');
    const depClock = formatHour(depHour);
    const arriveClock = formatHour(arrivalHour);
    const hasData = arrivalAvg != null;
    const answer = hasData
        ? t('answer', { depClock, arriveClock, avg: arrivalAvg })
        : t('answer_nodata', { depClock, arriveClock });
    let relation = t('relation_average');
    if (hasData && dayAvg != null) {
        if (arrivalAvg > dayAvg + 0.5)
            relation = t('relation_busier');
        else if (arrivalAvg < dayAvg - 0.5)
            relation = t('relation_calmer');
    }
    // Prev/next hour links for crawlable sequential internal linking.
    const i = DEPARTURE_HOURS.indexOf(depHour);
    const prevHour = i > 0 ? DEPARTURE_HOURS[i - 1] : null;
    const nextHour = i < DEPARTURE_HOURS.length - 1 ? DEPARTURE_HOURS[i + 1] : null;
    const sourceUrl = CPH_CHECKIN_URL[lang] || CPH_CHECKIN_URL.en;
    const faqs = [
        { q: t('faq_q1', { depClock }), a: t('faq_a1', { depClock, arriveClock }) },
        { q: t('faq_q2'), a: t('faq_a2') },
        { q: t('faq_q3', { depClock }), a: t('faq_a3', { arriveClock }) }
    ];
    const faqJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map(({ q, a }) => ({
            '@type': 'Question',
            name: q,
            acceptedAnswer: { '@type': 'Answer', text: a }
        }))
    };
    return (<div className="bg-white p-4 sm:p-6 my-auto rounded max-w-3xl mx-auto text-gray-800">
            <Head>
                <title>{t('meta_title', { depClock })}</title>
                <meta name="description" content={t('meta_description', { depClock, arriveClock })}/>
                {/* Thin-content guard: noindex pages we cannot ground in data. */}
                <meta name="robots" content={hasData ? 'all' : 'noindex,follow'}/>
                <meta property="og:title" content={t('meta_title', { depClock })}/>
                <meta property="og:description" content={t('meta_description', { depClock, arriveClock })}/>
                <script type="application/ld+json" dangerouslySetInnerHTML={{
            __html: JSON.stringify(faqJsonLd).replace(/</g, '\\u003c')
        }}/>
            </Head>

            <h1 className="text-2xl sm:text-3xl font-bold">
                {t('page_title', { depClock })}
            </h1>

            {/* Answer-first, data-bearing statement (GEO-extractable). */}
            <p className="mt-4 text-lg">{answer}</p>
            {hasData && dayAvg != null && (<p className="mt-2 text-gray-700">
                    {t('vs_day', { dayAvg, relation })}
                </p>)}

            <div className="mt-6">
                <Link href="/" className="text-[var(--secondary)] underline">
                    {t('cta_live')}
                </Link>
            </div>

            {/* Lead-time guidance (general CPH guidance, attributed). */}
            <h2 className="text-xl font-semibold mt-8">{t('lead_heading')}</h2>
            <p className="mt-2">{t('lead_body', { depClock, arriveClock })}</p>
            <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-sm text-[var(--secondary)] underline">
                {t('source')}
            </a>

            {/* The arrival window: real average waits around your arrival time. */}
            <h2 className="text-xl font-semibold mt-8">
                {t('window_table_title', { arriveClock })}
            </h2>
            <table className="mt-3 w-full text-left border-collapse">
                <thead>
                    <tr className="border-b">
                        <th className="py-2">{t('col_time')}</th>
                        <th className="py-2">{t('col_avg')}</th>
                    </tr>
                </thead>
                <tbody>
                    {windowHours.map((w) => (<tr key={w.hour} className={`border-b ${w.recommended ? 'font-semibold' : ''}`}>
                            <td className="py-2">
                                {formatHour(w.hour)}
                                {w.recommended
                ? ` (${t('recommended_label')})`
                : ''}
                            </td>
                            <td className="py-2">
                                {w.avgLow != null ? w.avgLow : '-'}
                            </td>
                        </tr>))}
                </tbody>
            </table>

            <p className="mt-4 text-xs text-gray-500">{t('source_note')}</p>

            {/* Sequential + related internal links. */}
            <div className="mt-8 flex flex-wrap gap-4 text-sm">
                {prevHour != null && (<Link href={`/best-time-to-arrive/${pad(prevHour)}`} className="text-[var(--secondary)] underline">
                        {t('prev_hour', { clock: formatHour(prevHour) })}
                    </Link>)}
                {nextHour != null && (<Link href={`/best-time-to-arrive/${pad(nextHour)}`} className="text-[var(--secondary)] underline">
                        {t('next_hour', { clock: formatHour(nextHour) })}
                    </Link>)}
            </div>

            <div className="mt-6 flex flex-col gap-2">
                <Link href="/best-time-to-arrive" className="text-[var(--secondary)] underline">
                    {t('cta_all_hours')}
                </Link>
                <Link href="/security-opening-hours" className="text-[var(--secondary)] underline">
                    {t('cta_opening_hours')}
                </Link>
                <Link href="/travel-times" className="text-[var(--secondary)] underline">
                    {t('cta_weekday')}
                </Link>
            </div>

            <h2 className="text-xl font-semibold mt-8">FAQ</h2>
            <dl className="mt-3 divide-y divide-gray-200">
                {faqs.map(({ q, a }, i) => (<div key={i} className="py-3">
                        <dt className="font-semibold">{q}</dt>
                        <dd className="mt-1 text-gray-700">{a}</dd>
                    </div>))}
            </dl>
        </div>);
}
export default BestTimeToArrive;

    async function __Next_Translate__getStaticProps__1a1723ec6f4__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/best-time-to-arrive/[hour]',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a1723ec6f4__ as getStaticProps }
  